import { Injector, Directive, OnDestroy, signal, computed, output, untracked } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isDefined } from '@fi-shared/utils';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { toObservable } from '@angular/core/rxjs-interop';

export interface FiDataSourceSettings {
    reverseMode?: boolean;
}

export interface ILoadDataArgs {
    fetchOnlyItems?: boolean;
    fetchParams?: {
        // use only from FiRemoteDataSource
        offset: number;
        numberOfRecordsToTake: number;
    };
}

export interface IItemsListReplaceData<T> {
    oldItem: T;
    newItem: T;
}

@Directive()
export abstract class FiDataSourceBase<T> implements OnDestroy {
    public selectionChanged = output<T[]>();

    protected numberOfRecordsToTake = FiAppSettings.NumberOfRecordsPerPage;

    private _items = signal<T[]>([]);
    public items = this._items.asReadonly();
    public items$ = toObservable(this._items);
    public setItems(items: T[]) {
        this._items.set(items);
    }
    public updateItems(updateFn: (items: T[]) => T[]) {
        this._items.update((items) => updateFn(items));
    }
    public offset = computed(() => this._items().length);

    private _selectedItems = signal<T[]>([]);
    public selectedItems = this._selectedItems.asReadonly();
    public selectedItems$ = toObservable(this._selectedItems);

    private _totalCount: number;
    protected get totalCount() {
        return this._totalCount;
    }
    protected setTotalCount(newTotalCount: number) {
        this._totalCount = newTotalCount;
    }

    abstract loadItemsFn(loadArgs: ILoadDataArgs): Promise<T[]>; //  fetch next batch items

    protected constructor(
        protected injector: Injector,
        private settings?: FiDataSourceSettings,
    ) {
        if (!this.settings) {
            this.settings = {};
        }

        this.selectedItems$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((selectedItems) => this.selectionChanged.emit(selectedItems));
    }

    //region data managing
    private clearItems() {
        if (this.items().length > 0) {
            this._items.set([]);
        }
    }
    public clearSelectedItems() {
        if (this.selectedItems().length > 0) {
            this._selectedItems.set([]);
        }
    }

    protected clearData() {
        // prevent redundant reactions on signal updates
        this.clearItems();
        this.clearSelectedItems();
    }

    protected appendData(
        newData: T[],
        appendArgs?: {
            reverseMode?: boolean; // conversation messages have reverse mode (latest bottom to top)
            appendToTop?: boolean;
        },
    ) {
        if (!appendArgs) {
            appendArgs = {
                reverseMode: this.settings.reverseMode,
            };
        }
        let currentItems = this._items();
        if (appendArgs.appendToTop) {
            currentItems = newData.concat(currentItems);
        } else if (appendArgs.reverseMode) {
            currentItems = newData.reverse().concat(currentItems);
        } else {
            currentItems = currentItems.concat(newData);
        }
        this._items.set(currentItems);
    }

    protected removeData(dataToRemove: T[]) {
        const data = this._items().slice(0);
        const selectedItems = this._selectedItems().slice(0);
        dataToRemove.forEach((itemToRemove) => {
            const dataIdx = data.findIndex((item) => itemToRemove === item);
            if (dataIdx !== -1) {
                if (isDefined(this._totalCount)) {
                    this._totalCount--;
                }
                data.splice(dataIdx, 1);
            }
            selectedItems.splice(selectedItems.indexOf(itemToRemove), 1);
        });

        this._items.set(data);
        this._selectedItems.set(selectedItems);
    }

    protected replaceData(dataToReplace: IItemsListReplaceData<T>[]) {
        if (!dataToReplace.length) return;

        const items = this._items().slice(0);
        const selectedItems = this._selectedItems().slice(0);
        dataToReplace.forEach((dataToReplace) => {
            const itemIdx = items.findIndex((item) => dataToReplace.oldItem === item);
            if (itemIdx !== -1) {
                items.splice(itemIdx, 1, dataToReplace.newItem);
            }

            const selectedItemIdx = selectedItems.findIndex((item) => dataToReplace.oldItem === item);
            if (selectedItemIdx !== -1) {
                selectedItems.splice(selectedItemIdx, 1, dataToReplace.newItem);
            }
        });
        this._items.set(items);
        this._selectedItems.set(selectedItems);
    }

    //endregion

    //region data loading

    /*
    use this method from start or when sorting/filtering/searching changed
    */
    protected reloadData(loadArgs?: ILoadDataArgs) {
        if (!loadArgs) {
            loadArgs = {
                fetchOnlyItems: false,
            };
        }
        if (!loadArgs.fetchOnlyItems) {
            this._totalCount = undefined;
        }
        return untracked(() => {
            this.clearData();
            return this.loadNext(loadArgs);
        });
    }

    /* use this method for loading next part of data: on scroll to bottom etc */
    loadNext(loadArgs?: ILoadDataArgs) {
        if (!loadArgs) {
            loadArgs = {
                fetchOnlyItems: true,
            };
        }

        const resultPromise = new Promise<boolean>((resolve, reject) => {
            if (!this._loading() && this.canLoadMore()) {
                // start load data
                this._loading.set(true);

                this.loadItemsFn(
                    Object.assign(loadArgs, {
                        fetchParams: {
                            offset: this.offset(),
                            numberOfRecordsToTake: this.numberOfRecordsToTake,
                        },
                    }),
                )
                    .then((data) => {
                        this._loading.set(false);
                        this.appendData(data);

                        if (data.length < this.numberOfRecordsToTake) {
                            this._totalCount = this._items().length;
                        }

                        resolve(true);
                    }, reject)
                    .catch(() => {
                        this._loading.set(false);
                    });
            } else {
                // loading is in process or we have no more available items
                resolve(false);
            }
        });
        return resultPromise;
    }

    private _loading = signal<boolean>(false);
    public loading = this._loading.asReadonly();

    getLoadingValue(): boolean {
        return this._loading();
    }

    private canLoadMore() {
        return !isDefined(this.totalCount) || this.items().length < this.totalCount;
    }
    //endregion

    //region selection
    onSelectionChanged($event: T[]) {
        this._selectedItems.set($event);
    }
    //endregion

    //region destroy
    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
    //endregion
}
